// src/pages/UserManagement.js
// Updated: Tuesday, August 4, 2026 at 8:25 PM EDT

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, doc, deleteDoc, orderBy as firestoreOrderBy, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { setFinanceRole } from '../services/financeAdminService';

// Import our components
import UserEditModal from '../components/UserEditModal';

import {
  List, ListItem, ListItemText, Button, Container, Typography, CircularProgress, Divider, Paper,
  Accordion, AccordionSummary, AccordionDetails, Chip, Box, Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const functions = getFunctions();
const getUserEmailVerifiedStatus = httpsCallable(functions, 'getUserEmailVerifiedStatus');

function UserManagement() {
  const { currentUser, userProfile } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [allChurches, setAllChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserChurchPushEnabled, setSelectedUserChurchPushEnabled] = useState(false);


  useEffect(() => {
    if (!userProfile) {
      setLoading(false);
      return;
    }

    const usersColRef = collection(db, 'users');
    const isSuperAdmin = userProfile.role === 'Super Administrator';

    let pendingQuery;
    if (isSuperAdmin) {
        pendingQuery = query(usersColRef, where('status', '==', 'pending_approval'), firestoreOrderBy('lastName'), firestoreOrderBy('firstName'));
    } else {
        pendingQuery = query(usersColRef,
                             where('status', '==', 'pending_approval'),
                             where('churchId', '==', userProfile.churchId),
                             firestoreOrderBy('lastName'),
                             firestoreOrderBy('firstName'));
    }

    const unsubscribePending = onSnapshot(pendingQuery, snapshot => {
      let pending = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPendingUsers(pending);
      setLoading(false);
    });

    let unsubscribeActive = () => {};
    if (userProfile.churchId || isSuperAdmin) {
      const activeQuery = isSuperAdmin
        ? query(usersColRef, where('status', '==', 'active'), firestoreOrderBy('lastName'), firestoreOrderBy('firstName'))
        : query(usersColRef, where('churchId', '==', userProfile.churchId), where('status', '==', 'active'), firestoreOrderBy('lastName'), firestoreOrderBy('firstName'));

      unsubscribeActive = onSnapshot(activeQuery, (snapshot) => {
        let active = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        if (!isSuperAdmin) {
            active = active.filter(user => user.role !== 'Super Administrator');
        }
        setActiveUsers(active);
        setLoading(false);
      });
    }

    let unsubscribeChurches = () => {};
    let unsubscribeChurchDetails = () => {};
    if (isSuperAdmin) {
      const churchesQuery = query(collection(db, 'churches'), firestoreOrderBy('name', 'asc'));
      unsubscribeChurches = onSnapshot(churchesQuery, (snapshot) => {
        setAllChurches(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    } else if (userProfile.churchId) {
        const churchDocRef = doc(db, 'churches', userProfile.churchId);
        unsubscribeChurchDetails = onSnapshot(churchDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const churchData = docSnap.data();
                setAllChurches([{id: docSnap.id, ...churchData}]);
            }
        });
    }

    return () => {
      unsubscribePending();
      unsubscribeActive();
      unsubscribeChurches();
      unsubscribeChurchDetails();
    };
  }, [userProfile]);

  const handleEditUser = async (user) => {
    try {
        const result = await getUserEmailVerifiedStatus({ userId: user.id });
        const userWithAuthInfo = {
            ...user,
            emailVerified: result.data.emailVerified,
        };
        setSelectedUser(userWithAuthInfo);
    } catch (error) {
        console.error("Error fetching email verification status:", error);
        setSelectedUser(user);
    }

    setIsModalOpen(true);
    if (user?.churchId) {
      const churchDocRef = doc(db, 'churches', user.churchId);
      getDoc(churchDocRef).then(docSnap => {
        if (docSnap.exists()) {
          setSelectedUserChurchPushEnabled(docSnap.data().enablePushNotifications || false);
        } else {
          setSelectedUserChurchPushEnabled(false);
        }
      }).catch(error => {
        console.error("Error fetching selected user's church push notification setting:", error);
        setSelectedUserChurchPushEnabled(false);
      });
    } else {
      setSelectedUserChurchPushEnabled(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setSelectedUserChurchPushEnabled(false);
  };

  const handleSaveUser = async (updatedUserData) => {
    if (!selectedUser) return;

    const {
        email, phone, address, city, state, zip, birthday,
        authUid, churchId, createdAt, firstName, lastName, role, status,
        canSendNotifications, canViewPrivatePrayers, canEditHomePage, canViewInquiries,
        // NEW FIELDS: Extracting the new data passed from UserEditModal
        financeRole, householdId, financialIdentifiers, financeRoleChangeReason
    } = updatedUserData;

    const publicDataToUpdate = {
        authUid: authUid || selectedUser.authUid,
        churchId: churchId,
        createdAt: createdAt || selectedUser.createdAt,
        firstName: firstName,
        lastName: lastName,
        role: role,
        status: status,
        canSendNotifications: canSendNotifications || false,
        canViewPrivatePrayers: canViewPrivatePrayers || false,
        canEditHomePage: canEditHomePage || false,
        canViewInquiries: canViewInquiries || false,
        householdId: householdId || null,
        financialIdentifiers: financialIdentifiers || {}
    };

    const privateDataToUpdate = {
        email: email,
        phone: phone,
        address: address,
        city: city,
        state: state,
        zip: zip,
        birthday: birthday ? Timestamp.fromDate(new Date(birthday)) : null,
    };

    const userDocRef = doc(db, 'users', selectedUser.id);
    const privateContactInfoDocRef = doc(db, 'users', selectedUser.id, 'privateContactInfo', 'details');

    try {
        await setDoc(userDocRef, publicDataToUpdate, { merge: true });
        await setDoc(privateContactInfoDocRef, privateDataToUpdate, { merge: true });

        const oldFinanceRole = selectedUser.financeRole || '';
        const newFinanceRole = financeRole || '';
        if (oldFinanceRole !== newFinanceRole) {
          if (!financeRoleChangeReason?.trim()) {
            throw new Error('A reason is required to change financial access.');
          }
          await setFinanceRole({
            userId: selectedUser.id,
            financeRole: newFinanceRole,
            reason: financeRoleChangeReason.trim(),
          });
        }

        if (selectedUser.id === currentUser.uid) {
            await currentUser.getIdToken(true);
        }

        alert("User profile saved successfully!");
        handleCloseModal();
    } catch (error) {
        console.error("Error saving user profile:", error);
        alert("Failed to save user profile.");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!userId) return;
    if (window.confirm('Are you sure you want to deny and permanently delete this user? This action cannot be undone.')) {
        try {
            const userDocRef = doc(db, 'users', userId);
            const privateContactInfoDocRef = doc(db, 'users', userId, 'privateContactInfo', 'details');
            await deleteDoc(privateContactInfoDocRef).catch(error => console.warn("Could not delete private contact info:", error));

            await deleteDoc(userDocRef);
            alert('User has been denied and their request was deleted.');
            handleCloseModal();
        } catch (error) {
            console.error("Error deleting user:", error);
            alert(`Failed to delete user: ${error.message}`);
        }
    }
  };

  const groupedActiveUsersByChurch = useMemo(() => {
    const groups = {};
    activeUsers.forEach(user => {
      const church = allChurches.find(c => c.id === user.churchId);
      const churchName = church ? church.name : 'Unassigned / No Church';
      if (!groups[churchName]) groups[churchName] = [];
      groups[churchName].push(user);
    });
    Object.keys(groups).forEach(churchName => {
        groups[churchName].sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
    });
    return groups;
  }, [activeUsers, allChurches]);

  const groupedPendingUsersByChurch = useMemo(() => {
    const groups = {};
    pendingUsers.forEach(user => {
        const church = allChurches.find(c => c.id === user.churchId);
        const churchName = church ? church.name : 'Awaiting Church Assignment';
        if (!groups[churchName]) groups[churchName] = [];
        groups[churchName].push(user);
    });
    Object.keys(groups).forEach(churchName => {
        groups[churchName].sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
    });
    return groups;
  }, [pendingUsers, allChurches]);


  if (loading && allChurches.length === 0) return <CircularProgress />;

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>User Management</Typography>

      <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
        <Typography variant="h5" gutterBottom>Pending Approval ({pendingUsers.length})</Typography>
        {Object.keys(groupedPendingUsersByChurch).length === 0 ? (
          <Typography sx={{ p: 2 }}>No users pending approval.</Typography>
        ) : (
          Object.keys(groupedPendingUsersByChurch).sort((a,b) => a.localeCompare(b)).map(churchName => (
            <Accordion key={churchName} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{churchName} ({groupedPendingUsersByChurch[churchName].length})</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List>
                  {groupedPendingUsersByChurch[churchName].map((user, i) => (
                    <React.Fragment key={user.id}>
                      <ListItem secondaryAction={<Button onClick={() => handleEditUser(user)}>Manage</Button>}>
                        <ListItemText primary={`${user.firstName} ${user.lastName}`} secondary={user.email} />
                      </ListItem>
                      {i < groupedPendingUsersByChurch[churchName].length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>Active Members ({activeUsers.length})</Typography>
        {Object.keys(groupedActiveUsersByChurch).length === 0 && (
          <Typography sx={{ p: 2 }}>No active members found.</Typography>
        )}
        {Object.keys(groupedActiveUsersByChurch).sort((a,b) => a.localeCompare(b)).map(churchName => (
          <Accordion key={churchName} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{churchName} ({groupedActiveUsersByChurch[churchName].length})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List>
                {groupedActiveUsersByChurch[churchName].map((user, i) => (
                  <React.Fragment key={user.id}>
                    <ListItem secondaryAction={<Button onClick={() => handleEditUser(user)}>Edit</Button>}>
                      <ListItemText
                        primary={`${user.firstName} ${user.lastName}`}
                        secondaryTypographyProps={{ component: 'div' }}
                        secondary={
                          <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Typography component="span" variant="body2" color="text.secondary" display="block">
                                Role: {user.role}
                            </Typography>
                            {user.email && !user.emailVerified && (
                              <Chip
                                label="Not Verified"
                                color="warning"
                                size="small"
                                sx={{ml: 1}}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {i < groupedActiveUsersByChurch[churchName].length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <UserEditModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
        onDelete={handleDeleteUser}
        user={selectedUser}
        adminChurchId={userProfile?.churchId}
        editorProfile={userProfile}
        allChurches={allChurches}
        isChurchPushNotificationsEnabled={selectedUserChurchPushEnabled}
      />
    </Container>
  );
}

export default UserManagement;